import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const CurrencyInflationAdjusterCalculator = () => {
  const [amount, setAmount] = useState(100);
  const [startYear, setStartYear] = useState(1957);
  const [endYear, setEndYear] = useState(2025);
  const [inflationRate, setInflationRate] = useState(7.11);
  const [result, setResult] = useState({
    futureValue: "0.00",
    totalInflation: "0.00",
    purchasingPowerLoss: "0.00",
    averageAnnualInflation: "0.00",
    yearsElapsed: "0",
  });
  const [chartData, setChartData] = useState([]);

  // Currency Inflation Calculation Function
  const calculateInflation = () => {
    const initialAmount = parseFloat(amount);
    const start = parseInt(startYear);
    const end = parseInt(endYear);
    const annualRate = parseFloat(inflationRate) / 100;

    if (isNaN(initialAmount) || isNaN(start) || isNaN(end) || isNaN(annualRate) || initialAmount <= 0) return;

    if (end <= start) {
      alert("End year must be greater than start year");
      return;
    }

    const years = end - start;
    
    // Calculate future value using compound inflation formula: FV = PV * (1 + r)^n
    const futureValue = initialAmount * Math.pow(1 + annualRate, years);
    const totalInflationPercent = ((futureValue - initialAmount) / initialAmount) * 100;
    const purchasingPowerLoss = ((initialAmount / futureValue) * 100);

    // Generate chart data
    const yearlyData = [];
    for (let i = 0; i <= Math.min(years, 50); i += Math.max(1, Math.floor(years / 20))) {
      const year = start + i;
      const value = initialAmount * Math.pow(1 + annualRate, i);
      yearlyData.push({
        year: year,
        value: parseFloat(value.toFixed(2)),
        purchasingPower: parseFloat((initialAmount / value * 100).toFixed(2))
      });
    }
    
    // Ensure end year is included
    if (yearlyData[yearlyData.length - 1].year !== end) {
      yearlyData.push({
        year: end,
        value: parseFloat(futureValue.toFixed(2)),
        purchasingPower: parseFloat(purchasingPowerLoss.toFixed(2))
      });
    }

    setChartData(yearlyData);

    setResult({
      futureValue: futureValue.toFixed(2),
      totalInflation: totalInflationPercent.toFixed(2),
      purchasingPowerLoss: (100 - purchasingPowerLoss).toFixed(2),
      averageAnnualInflation: (annualRate * 100).toFixed(2),
      yearsElapsed: years.toString(),
    });
  };

  // Run default calculation on first load
  useEffect(() => {
    calculateInflation();
  }, []);

  const currentYear = new Date().getFullYear();

  // Pie chart data for purchasing power breakdown
  const pieData = [
    { name: 'Purchasing Power Lost', value: parseFloat(result.purchasingPowerLoss) },
    { name: 'Remaining Value', value: 100 - parseFloat(result.purchasingPowerLoss) }
  ];

  const COLORS = ['#ef4444', '#10b981'];

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <h1>Currency Inflation Calculator</h1>
          <p>
            Calculate how inflation affects your money's purchasing power over time and see what currency was worth in the past
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="16"></line>
                  <line x1="8" y1="12" x2="16" y2="12"></line>
                </svg>
                Inflation Details
              </h2>

              {/* Amount */}
              <div className="input-group">
                <label>Amount</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="calc-input"
                    placeholder="100"
                  />
                </div>
                <div className="quick-select-group">
                  {[100, 1000, 10000, 100000].map((amt) => (
                    <button
                      key={amt}
                      onClick={() => setAmount(amt)}
                      className="quick-select-btn primary"
                    >
                      ₹{amt >= 1000 ? (amt / 1000).toFixed(0) + 'K' : amt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Start Year */}
              <div className="input-group">
                <label>Start Year</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={startYear}
                    onChange={(e) => setStartYear(e.target.value)}
                    className="calc-input"
                    placeholder="1957"
                    min="1900"
                    max={currentYear}
                  />
                </div>
                <div className="quick-select-group">
                  {[1950, 1975, 2000, 2010].map((year) => (
                    <button
                      key={year}
                      onClick={() => setStartYear(year)}
                      className="quick-select-btn secondary"
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>

              {/* End Year */}
              <div className="input-group">
                <label>End Year</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={endYear}
                    onChange={(e) => setEndYear(e.target.value)}
                    className="calc-input"
                    placeholder="2025"
                    min="1900"
                    max={currentYear + 50}
                  />
                </div>
                <div className="quick-select-group">
                  {[2020, 2025, 2030, 2040].map((year) => (
                    <button
                      key={year}
                      onClick={() => setEndYear(year)}
                      className="quick-select-btn success"
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>

              {/* Average Annual Inflation Rate */}
              <div className="input-group">
                <label>Average Annual Inflation Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={inflationRate}
                    onChange={(e) => setInflationRate(e.target.value)}
                    className="calc-input"
                    placeholder="7.11"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>

              <button onClick={calculateInflation} className="calculate-btn">
                Calculate Inflation
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Inflation Summary
              </h2>

              <div className="result-card">
                {/* Future Value - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Equivalent Value in {endYear}</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.futureValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    ₹{Number(amount).toLocaleString("en-IN")} in {startYear}
                  </div>
                </div>

                {/* Total Inflation */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Inflation</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value" style={{fontSize: '1.75rem'}}>
                    {result.totalInflation}%
                  </div>
                  <div className="result-subtext">
                    Over {result.yearsElapsed} years
                  </div>
                </div>

                {/* Purchasing Power Loss */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Purchasing Power Lost</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#ef4444'}}>
                        <polyline points="22 17 13.5 8.5 8.5 13.5 2 7"></polyline>
                        <polyline points="16 17 22 17 22 11"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.purchasingPowerLoss}%
                  </div>
                  <div className="result-subtext">
                    Money lost its value
                  </div>
                </div>

                {/* Average Annual Rate */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Average Annual Rate</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.averageAnnualInflation}%
                  </div>
                  <div className="result-subtext">
                    Per year inflation
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Purchasing Power Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Value Lost to Inflation</span>
                      <span className="breakdown-percentage">
                        {result.purchasingPowerLoss}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill"
                        style={{
                          width: `${result.purchasingPowerLoss}%`,
                          backgroundColor: '#ef4444'
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Remaining Purchasing Power</span>
                      <span className="breakdown-percentage" style={{color: '#10b981'}}>
                        {(100 - parseFloat(result.purchasingPowerLoss)).toFixed(2)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${100 - parseFloat(result.purchasingPowerLoss)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="calculator-card" style={{marginTop: '2rem'}}>
          <h2 className="section-title" style={{marginBottom: '1.5rem'}}>
            <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
            Visual Analysis
          </h2>

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem'}}>
            {/* Line Chart */}
            <div>
              <h3 style={{fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937'}}>
                Currency Value Over Time
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip formatter={(value) => `₹${Number(value).toLocaleString("en-IN")}`} />
                  <Legend />
                  <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} name="Equivalent Value" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Pie Chart */}
            <div>
              <h3 style={{fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937'}}>
                Purchasing Power Distribution
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({name, value}) => `${name}: ${value.toFixed(1)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${Number(value).toFixed(2)}%`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the Currency Inflation Calculator Work?</h3>
              <p>
                The currency inflation calculator shows how the purchasing power of money changes over time 
                due to inflation. It uses historical or projected inflation rates to calculate what a specific 
                amount of money from the past would be worth today, or what today's money will be worth in the 
                future. This helps you understand the real value of money across different time periods.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Formula Used:</h4>
            <div className="formula-display">
              FV = PV × (1 + r)ⁿ
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">FV</div>
                <div className="formula-param-value">Future Value (Equivalent amount)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">PV</div>
                <div className="formula-param-value">Present Value (Original amount)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Annual Inflation Rate</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Number of Years</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is inflation and why does it matter?</h5>
              <p>
                Inflation is the rate at which the general level of prices for goods and services rises, 
                eroding purchasing power over time. If inflation is 7% annually, something that costs ₹100 
                today will cost ₹107 next year. This matters because your savings lose value if they don't 
                grow faster than inflation. Understanding inflation helps you make better financial decisions 
                about savings, investments, and retirement planning.
              </p>
            </div>

            <div className="faq-item">
              <h5>What has been India's historical inflation rate?</h5>
              <p>
                India's inflation rate has varied significantly over the decades. From 1957 to 2025, the 
                average annual inflation rate has been approximately 7.11%, though it's fluctuated considerably. 
                The 1970s saw high inflation above 10%, while recent years (2010-2025) have averaged around 
                5-6%. The Reserve Bank of India now targets inflation at 4% ±2%, aiming for more stable prices 
                and predictable purchasing power.
              </p>
            </div>

            <div className="faq-item">
              <h5>How does inflation affect my savings?</h5>
              <p>
                Inflation directly reduces your savings' purchasing power. If you have ₹100,000 in a savings 
                account earning 4% interest but inflation is 6%, you're actually losing 2% purchasing power 
                annually. After 10 years, your money might grow to ₹148,024, but you'd need ₹179,085 to buy 
                what ₹100,000 bought initially. This is why investing in assets that beat inflation (like 
                equity, real estate) is crucial for long-term wealth preservation.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is the difference between nominal and real returns?</h5>
              <p>
                Nominal returns are the stated returns on your investment without considering inflation, 
                while real returns account for inflation's impact. If your investment gives 12% returns 
                (nominal) but inflation is 6%, your real return is approximately 6%. Real returns represent 
                your actual increase in purchasing power. Always focus on real returns when evaluating 
                long-term investments, as they show your true wealth growth.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I protect my money from inflation?</h5>
              <p>
                Protect against inflation by investing in assets that historically outpace it: equity mutual 
                funds (10-12% returns), real estate, gold, and inflation-indexed bonds. Diversify across 
                asset classes based on your time horizon. For short-term needs (under 3 years), use debt 
                instruments. For long-term goals (5+ years), allocate more to equity. Also, increase your 
                income through skills and career growth to stay ahead of rising costs.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I adjust my retirement planning for inflation?</h5>
              <p>
                Absolutely! Inflation is critical for retirement planning. If you need ₹50,000/month today, 
                you'll need approximately ₹97,000/month after 20 years at 3% inflation, or ₹1,61,000/month 
                at 6% inflation. Calculate your retirement corpus considering inflation-adjusted expenses. 
                Use conservative inflation estimates (6-7%) and ensure your investments can generate 
                inflation-beating returns. Review and adjust your retirement plan every 3-5 years.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrencyInflationAdjusterCalculator;